import { useMemo, type JSX } from 'react';

import { PriceSimulationHousingTypeDto } from '@eneco/dc-be/client';
import mapScRadioToMap from '@eneco/dc-be/utils/mapScRadioToMap';
import { Button, Heading, RadioGroup, RadioTile, Stack } from '@eneco/sparky';
import { Apartment, DetachedHouse, SemiDetachedHouse, TerracedHouse } from '@eneco/sparky/illustrations';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { HOUSING_TYPE_OPTIONS, PriceSimulationFormSchema } from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

const getPropertyTypeImage = (option: PriceSimulationHousingTypeDto) => {
  switch (option) {
    case 'Apartment':
      return <Apartment size="small" />;
    case 'FullyEnclosedBuilding':
      return <TerracedHouse size="small" />;
    case 'HalfOpenBuilding':
      return <SemiDetachedHouse size="small" />;
    case 'OpenBuilding':
      return <DetachedHouse size="small" />;
  }
};

export const PropertyTypeCheckStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    control,
    trigger,
    formState: { errors },
    getValues,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const propertyTypeOptions = HOUSING_TYPE_OPTIONS.map(option => ({
    name: option,
    label: option,
  }));

  const sitecorePropertyTypeOptions = mapScRadioToMap(fields.propertyTypeStep.propertyTypesList);

  const propertyTypeOptionsMap = useMemo(() => {
    const map = new Map<PriceSimulationHousingTypeDto, { label: string; name: string; image?: JSX.Element }>();
    for (const option of propertyTypeOptions) {
      map.set(option.name, {
        label: sitecorePropertyTypeOptions.get(option.name) ?? option.name + ' [not in Sitecore]',
        name: option.name,
        image: getPropertyTypeImage(option.name),
      });
    }
    return map;
  }, [sitecorePropertyTypeOptions, propertyTypeOptions]);

  const onNextClick = async () => {
    const isValid = await trigger('propertyTypeCheck');
    if (isValid) {
      trackEventBE('pbt_calculation_unknown - housing', {
        status: 'click',
        data: getTrackableFormValues(getValues()),
      });
      nextMainStep();
    }
  };

  return (
    <>
      <Stack gap="2">
        <Heading as="h1" size="M">
          {fields.propertyTypeStep.title.value}
        </Heading>
      </Stack>
      <Controller
        control={control}
        name={'propertyTypeCheck'}
        render={({ field: { onChange, value } }) => (
          <RadioGroup
            label={fields.propertyTypeStep.propertyTypesList.value.label}
            name={'propertyTypeCheck'}
            isFullWidth
            value={value}
            onValueChange={onChange}
            error={
              errors.propertyTypeCheck?.message
                ? fields.propertyTypeStep.propertyTypesList.value.requiredMessage
                : undefined
            }>
            {Array.from(propertyTypeOptionsMap.values()).map(({ name, label, image }) => (
              <RadioTile key={name} icon={image} value={name}>
                {label}
              </RadioTile>
            ))}
          </RadioGroup>
        )}
      />
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
